/*
  <otw-slider> — CSS scroll-snap carousel (PRD §9).

  <otw-slider data-autoplay="5000" data-loop data-arrows data-dots data-peek data-drag
              aria-label="Featured offers" style="--otw-slides: 1; --otw-slides-lg: 3;">
    <div class="otw-slider__track">
      <div class="otw-slider__slide">…</div>
    </div>
  </otw-slider>

  Options (data attributes; present = on, "false" = off):
    data-autoplay  ms between slides (bare attribute = 5000). Never runs under reduced motion.
    data-loop      wrap from last to first (scroll reset) and vice versa
    data-arrows    prev/next buttons (desktop only, fade in on hover, 200ms)
    data-dots      pagination dots, updated on scroll
    data-peek      show part of the next slide (bare = 10%, or a px number)
    data-drag      mouse drag with momentum on desktop
  Events: otw:slider:change { index, count }.
*/
(() => {
  if (customElements.get('otw-slider')) return;

  const OTW = (window.OTW = window.OTW || {});
  const t = (key, fallback) => (OTW.strings && OTW.strings[key]) || fallback;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const chevron = (d) =>
    `<svg class="otw-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="${d}"/></svg>`;

  class OtwSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.otw-slider__track');
      if (!this.track || this.initialised) return;
      this.initialised = true;
      this.abort = new AbortController();

      this.opts = {
        autoplay: this.readAutoplay(),
        loop: this.flag('loop'),
        arrows: this.flag('arrows'),
        dots: this.flag('dots'),
        drag: this.flag('drag'),
      };
      const peek = this.dataset.peek;
      if (peek && /^\d+$/.test(peek)) this.style.setProperty('--otw-peek', `${peek}px`);

      this.index = 0;
      this.positions = [0];
      this.paused = new Set();

      this.setupA11y();
      if (this.opts.arrows) this.buildArrows();
      if (this.opts.dots) this.buildDots();

      this.onScroll = this.onScroll.bind(this);
      this.listen(this.track, 'scroll', this.onScroll, { passive: true });
      this.listen(this.track, 'keydown', (e) => this.onKeydown(e));

      this.resizeObserver = new ResizeObserver(() => this.measure());
      this.resizeObserver.observe(this.track);
      this.mutationObserver = new MutationObserver(() => {
        this.setupA11y();
        this.measure();
      });
      this.mutationObserver.observe(this.track, { childList: true });

      if (this.opts.drag) this.setupDrag();
      this.setupAutoplay();
      this.measure();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      this.abort?.abort();
      this.resizeObserver?.disconnect();
      this.mutationObserver?.disconnect();
      this.intersection?.disconnect();
      reduced.removeEventListener?.('change', this.onReducedChange);
      // Generated controls are rebuilt if the element is reconnected
      this.prevButton?.remove();
      this.nextButton?.remove();
      this.dotsEl?.remove();
      this.prevButton = this.nextButton = this.dotsEl = null;
      this.initialised = false;
    }

    listen(target, type, handler, options = {}) {
      target.addEventListener(type, handler, { ...options, signal: this.abort.signal });
    }

    flag(name) {
      const value = this.dataset[name];
      return value !== undefined && value !== 'false';
    }

    readAutoplay() {
      const value = this.dataset.autoplay;
      if (value === undefined || value === 'false' || value === '0') return 0;
      const ms = parseInt(value, 10);
      return Number.isFinite(ms) && ms > 0 ? Math.max(1500, ms) : 5000;
    }

    get slides() {
      return Array.from(this.track.children).filter((el) => !el.hidden);
    }

    get count() {
      return this.positions.length;
    }

    /* ---------- Setup ---------- */
    setupA11y() {
      if (!this.hasAttribute('role')) this.setAttribute('role', 'region');
      this.setAttribute('aria-roledescription', t('carousel', 'carousel'));
      this.track.setAttribute('tabindex', '0');
      this.track.setAttribute('aria-live', this.opts.autoplay && !reduced.matches ? 'off' : 'polite');
      const slides = this.slides;
      slides.forEach((slide, i) => {
        slide.classList.add('otw-slider__slide');
        slide.setAttribute('role', 'group');
        slide.setAttribute('aria-roledescription', t('slide', 'slide'));
        slide.setAttribute(
          'aria-label',
          t('slideLabel', '[index] of [count]').replace('[index]', i + 1).replace('[count]', slides.length)
        );
      });
    }

    buildArrows() {
      const make = (dir) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = `otw-slider__arrow otw-slider__arrow--${dir}`;
        button.setAttribute('aria-label', dir === 'prev' ? t('previous', 'Previous slide') : t('next', 'Next slide'));
        button.innerHTML = chevron(dir === 'prev' ? 'm15 18-6-6 6-6' : 'm9 18 6-6-6-6');
        button.addEventListener('click', () => (dir === 'prev' ? this.prev() : this.next()));
        this.appendChild(button);
        return button;
      };
      this.prevButton = make('prev');
      this.nextButton = make('next');
    }

    buildDots() {
      this.dotsEl = document.createElement('div');
      this.dotsEl.className = 'otw-slider__dots';
      this.dotsEl.addEventListener('click', (event) => {
        const dot = event.target.closest('.otw-slider__dot');
        if (dot) this.goTo(Number(dot.dataset.index));
      });
      this.appendChild(this.dotsEl);
    }

    renderDots() {
      if (!this.dotsEl) return;
      const count = this.count;
      this.dotsEl.hidden = count < 2;
      if (this.dotsEl.children.length !== count) {
        this.dotsEl.innerHTML = '';
        for (let i = 0; i < count; i++) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'otw-slider__dot';
          dot.dataset.index = i;
          dot.setAttribute('aria-label', t('goTo', 'Go to slide [index]').replace('[index]', i + 1));
          this.dotsEl.appendChild(dot);
        }
      }
      Array.from(this.dotsEl.children).forEach((dot, i) => {
        dot.classList.toggle('is-active', i === this.index);
        if (i === this.index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    /* Snap positions: one per slide start, capped at the max scroll. Multi-up layouts get fewer pages. */
    measure() {
      const track = this.track;
      const max = Math.max(0, track.scrollWidth - track.clientWidth);
      const padStart = parseFloat(getComputedStyle(track).scrollPaddingInlineStart) || 0;
      const base = track.getBoundingClientRect().left - track.scrollLeft;
      const positions = [];
      for (const slide of this.slides) {
        const left = Math.round(Math.min(max, Math.max(0, slide.getBoundingClientRect().left - base - padStart)));
        if (!positions.length || left - positions[positions.length - 1] > 2) positions.push(left);
        if (left >= max) break;
      }
      this.positions = positions.length ? positions : [0];
      this.max = max;
      this.classList.toggle('is-static', max < 2);
      this.updateIndex();
      this.renderDots();
      this.updateArrows();
    }

    nearestIndex(left) {
      let best = 0;
      let distance = Infinity;
      this.positions.forEach((pos, i) => {
        const d = Math.abs(pos - left);
        if (d < distance) {
          distance = d;
          best = i;
        }
      });
      return best;
    }

    updateIndex() {
      const left = this.track.scrollLeft;
      const index = left >= this.max - 2 ? this.count - 1 : this.nearestIndex(left);
      if (index !== this.index) {
        this.index = index;
        this.dispatchEvent(new CustomEvent('otw:slider:change', { bubbles: true, detail: { index, count: this.count } }));
      }
    }

    updateArrows() {
      if (!this.prevButton) return;
      const atStart = this.track.scrollLeft <= 2;
      const atEnd = this.track.scrollLeft >= this.max - 2;
      this.prevButton.disabled = !this.opts.loop && atStart;
      this.nextButton.disabled = !this.opts.loop && atEnd;
      this.prevButton.hidden = this.nextButton.hidden = this.max < 2;
    }

    onScroll() {
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.updateIndex();
        this.renderDots();
        this.updateArrows();
      });
    }

    /* ---------- Navigation ---------- */
    goTo(index, { smooth = true } = {}) {
      const count = this.count;
      if (!count) return;
      let target = index;
      if (this.opts.loop) target = ((index % count) + count) % count;
      else target = Math.max(0, Math.min(count - 1, index));
      this.track.scrollTo({ left: this.positions[target], behavior: smooth && !reduced.matches ? 'smooth' : 'auto' });
      this.restartAutoplay();
    }

    next() {
      this.goTo(this.index + 1);
    }

    prev() {
      this.goTo(this.index - 1);
    }

    onKeydown(event) {
      if (event.target !== this.track) return;
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.next();
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.prev();
      }
    }

    /* ---------- Mouse drag with momentum ---------- */
    setupDrag() {
      const track = this.track;
      let startX = 0;
      let startLeft = 0;
      let lastX = 0;
      let lastT = 0;
      let velocity = 0;
      let moved = false;
      let active = false;

      this.listen(track, 'dragstart', (e) => e.preventDefault());

      this.listen(track, 'pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0 || this.max < 2) return;
        active = true;
        moved = false;
        startX = lastX = event.clientX;
        startLeft = track.scrollLeft;
        lastT = event.timeStamp;
        velocity = 0;
        this.paused.add('drag');
        this.stopAutoplay();
      });

      this.listen(window, 'pointermove', (event) => {
        if (!active) return;
        const dx = event.clientX - startX;
        if (!moved && Math.abs(dx) > 5) {
          moved = true;
          this.classList.add('is-dragging');
        }
        if (!moved) return;
        const dt = Math.max(1, event.timeStamp - lastT);
        velocity = 0.8 * ((event.clientX - lastX) / dt) + 0.2 * velocity;
        lastX = event.clientX;
        lastT = event.timeStamp;
        track.scrollLeft = startLeft - dx;
      });

      const end = () => {
        if (!active) return;
        active = false;
        this.paused.delete('drag');
        if (!moved) {
          this.restartAutoplay();
          return;
        }
        // Project the release velocity (px/ms) forward, then settle on the nearest snap point
        const projected = track.scrollLeft - velocity * 220;
        let index = this.nearestIndex(projected);
        if (index === this.index && Math.abs(velocity) > 0.3) index += velocity < 0 ? 1 : -1;
        index = Math.max(0, Math.min(this.count - 1, index));
        track.scrollTo({ left: this.positions[index], behavior: reduced.matches ? 'auto' : 'smooth' });
        const settle = () => this.classList.remove('is-dragging');
        if ('onscrollend' in window) track.addEventListener('scrollend', settle, { once: true });
        setTimeout(settle, 600);
        this.restartAutoplay();
      };
      this.listen(window, 'pointerup', end);
      this.listen(window, 'pointercancel', end);

      // Swallow the click that ends a drag so links inside slides don't fire
      this.listen(track,
        'click',
        (event) => {
          if (moved) {
            event.preventDefault();
            event.stopPropagation();
            moved = false;
          }
        },
        { capture: true }
      );
    }

    /* ---------- Autoplay ---------- */
    setupAutoplay() {
      this.onReducedChange = () => {
        this.track.setAttribute('aria-live', this.opts.autoplay && !reduced.matches ? 'off' : 'polite');
        this.restartAutoplay();
      };
      reduced.addEventListener?.('change', this.onReducedChange);
      if (!this.opts.autoplay) return;

      const pause = (reason) => () => {
        this.paused.add(reason);
        this.stopAutoplay();
      };
      const resume = (reason) => () => {
        this.paused.delete(reason);
        this.restartAutoplay();
      };

      this.listen(this, 'mouseenter', pause('hover'));
      this.listen(this, 'mouseleave', resume('hover'));
      this.listen(this, 'focusin', pause('focus'));
      this.listen(this, 'focusout', (event) => {
        if (!this.contains(event.relatedTarget)) resume('focus')();
      });
      this.listen(this, 'touchstart', pause('touch'), { passive: true });
      this.listen(this,
        'touchend',
        () => {
          clearTimeout(this.touchTimer);
          this.touchTimer = setTimeout(resume('touch'), 4000);
        },
        { passive: true }
      );

      this.onVisibility = () => (document.hidden ? pause('hidden')() : resume('hidden')());
      this.listen(document, 'visibilitychange', this.onVisibility);

      this.intersection = new IntersectionObserver(([entry]) => {
        entry.isIntersecting ? resume('offscreen')() : pause('offscreen')();
      });
      this.intersection.observe(this);
    }

    stopAutoplay() {
      clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    restartAutoplay() {
      this.stopAutoplay();
      if (!this.opts.autoplay || reduced.matches || this.paused.size || this.count < 2) return;
      this.autoplayTimer = setTimeout(() => {
        if (!this.opts.loop && this.index >= this.count - 1) {
          // Without loop, autoplay still restarts from the beginning
          this.track.scrollTo({ left: 0, behavior: 'smooth' });
          this.restartAutoplay();
          return;
        }
        this.next();
      }, this.opts.autoplay);
    }
  }

  customElements.define('otw-slider', OtwSlider);
})();
